import { memo, useMemo, useState } from 'react';
import { version } from '../../package.json';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, NavPill } from '@werkstatt/ui';
import {
  LayoutDashboard,
  Users,
  Clock,
  CalendarX,
  BarChart3,
  Settings,
  Menu,
  Shield,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuthStore } from '../store/authStore';

interface NavItem {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  roles?: string[];
  exact?: boolean;
}

export const Navigation = memo(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const [collapsed, setCollapsed] = useState(false);

  const navItems = useMemo(() => {
    const items: NavItem[] = [
      { title: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
      { title: 'Mitarbeiter', icon: Users, path: '/employees' },
      { title: 'Zeitbuchungen', icon: Clock, path: '/time-entries' },
      { title: 'Abwesenheiten', icon: CalendarX, path: '/absences' },
      { title: 'Berichte', icon: BarChart3, path: '/reports' },
    ];
    if (user?.role === 'admin') {
      items.push({ title: 'Einstellungen', icon: Settings, path: '/settings' });
    }
    return items;
  }, [user?.role]);

  const isActive = (item: NavItem) =>
    item.exact
      ? location.pathname === item.path
      : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);

  return (
    <div
      className={cn(
        'h-full transition-all duration-300 overflow-hidden rounded-card',
        'backdrop-blur-lg shadow-glass',
        'bg-[rgb(var(--sphere-card)_/_var(--sphere-card-alpha))]',
        'border border-[rgb(var(--sphere-border)_/_var(--sphere-border-alpha))]',
        collapsed ? 'w-20' : 'w-64'
      )}
    >
      <div className="flex flex-col justify-between h-full p-3">
        <div className="flex items-center justify-between px-1 py-2 mb-2">
          {!collapsed && (
            <div className="flex items-center space-x-2">
              {user?.role === 'super_admin'
                ? <Shield className="h-6 w-6 text-primary" />
                : <Clock className="h-6 w-6 text-primary" />}
              <span className="font-display font-semibold text-foreground">
                {user?.role === 'super_admin' ? 'Superadmin' : 'Zeiterfassung'}
              </span>
            </div>
          )}
          <Button variant="ghost" size="sm" className="p-1" onClick={() => setCollapsed(!collapsed)}>
            <Menu className="h-5 w-5 text-foreground" />
          </Button>
        </div>

        <nav className="flex-1 flex flex-col space-y-1">
          {navItems.map((item) => (
            <NavPill
              key={item.path}
              icon={item.icon}
              label={item.title}
              active={isActive(item)}
              collapsed={collapsed}
              onClick={() => navigate(item.path)}
            />
          ))}
        </nav>

        {!collapsed && (
          <div className="px-3 py-3 text-xs text-muted-foreground border-t border-[rgb(var(--sphere-border)_/_var(--sphere-border-alpha))]">
            Zeiterfassung v{version}
          </div>
        )}
      </div>
    </div>
  );
});

Navigation.displayName = 'Navigation';
